import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Icon } from '../../shared/Icons';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.ilouli.com';

const NewsBriefing = () => {
  const { t } = useTranslation();
  const { getToken, user } = useAuth();
  const [briefing, setBriefing] = useState(null);
  const [personalBriefing, setPersonalBriefing] = useState(null);
  const [themeBriefing, setThemeBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [themeLoading, setThemeLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('themes');

  // 일일 브리핑 로드
  const fetchBriefing = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setBriefing(data.briefing);
      } else {
        setError(data.error || '브리핑을 불러오는데 실패했습니다.');
      }
    } catch (err) {
      setError('브리핑을 불러오는데 실패했습니다.');
      console.error('Failed to load briefing:', err);
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  // 개인화 브리핑 로드
  const fetchPersonalBriefing = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/my`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setPersonalBriefing(data);
      }
    } catch (err) {
      console.error('Failed to load personal briefing:', err);
    }
  }, [getToken]);

  // 테마별 이슈 브리핑 로드
  const fetchThemeBriefing = useCallback(async (refresh = false) => {
    setThemeLoading(true);
    try {
      const params = refresh ? '?refresh=true' : '';
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/themes${params}`);

      const data = await response.json();
      if (data.success) {
        setThemeBriefing(data);
      }
    } catch (err) {
      console.error('Failed to load theme briefing:', err);
    } finally {
      setThemeLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBriefing();
    fetchPersonalBriefing();
    fetchThemeBriefing();
  }, [fetchBriefing, fetchPersonalBriefing, fetchThemeBriefing]);

  // 브리핑 재생성 (Admin)
  const handleRegenerate = async () => {
    if (user?.tier !== 'admin') return;

    setGenerating(true);
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE_URL}/api/news/briefing/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (data.success) {
        setBriefing(data.briefing);
      }
    } catch (err) {
      console.error('Failed to regenerate briefing:', err);
    } finally {
      setGenerating(false);
    }
  };

  // 날짜 포맷
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  // 시간 포맷
  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ko-KR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="max-w-[900px] mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
          <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
          <p className="text-gray-500">브리핑을 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-[900px] mx-auto p-6">
        <div className="flex flex-col items-center justify-center min-h-[300px] gap-4 text-gray-500">
          <Icon name="warning" size={48} className="text-amber-500" />
          <p>{error}</p>
          <button onClick={fetchBriefing} className="mt-2 py-2 px-6 bg-[#3B82F6] text-white border-none rounded-lg cursor-pointer text-[15px] hover:bg-[#2563EB]">다시 시도</button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto p-6 md:p-4">
      {/* 헤더 */}
      <div className="flex justify-between items-start mb-6 pb-4 border-b border-gray-200 md:flex-col md:gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 m-0 mb-1">오늘의 뉴스 브리핑</h1>
          <p className="text-[15px] text-gray-500 m-0">
            {briefing?.date ? formatDate(briefing.date) : ''}
          </p>
        </div>
        <div className="flex items-center gap-4 md:w-full md:justify-between">
          {briefing?.generatedAt && (
            <span className="text-xs text-gray-400">
              마지막 업데이트: {formatTime(briefing.generatedAt)}
            </span>
          )}
          {user?.tier === 'admin' && (
            <button
              className="inline-flex items-center gap-1 py-2 px-4 bg-[#3B82F6] text-white border border-[#3B82F6] rounded-lg text-xs cursor-pointer transition-all duration-200 hover:enabled:bg-[#2563EB] hover:enabled:border-[#2563EB] disabled:opacity-60 disabled:cursor-not-allowed"
              onClick={handleRegenerate}
              disabled={generating}
            >
              {generating ? '생성 중...' : <><Icon name="refresh" size={14} /> 재생성</>}
            </button>
          )}
        </div>
      </div>

      {/* 탭 */}
      <div className="flex gap-2 mb-6 md:flex-wrap">
        <button
          className={`inline-flex items-center gap-1 py-2 px-6 border rounded-lg text-[15px] cursor-pointer transition-all duration-200 md:flex-1 md:text-center md:justify-center ${activeTab === 'themes' ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-900'}`}
          onClick={() => setActiveTab('themes')}
        >
          <Icon name="fire" size={14} /> 테마별 이슈
        </button>
        <button
          className={`inline-flex items-center gap-1 py-2 px-6 border rounded-lg text-[15px] cursor-pointer transition-all duration-200 md:flex-1 md:text-center md:justify-center ${activeTab === 'daily' ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-900'}`}
          onClick={() => setActiveTab('daily')}
        >
          <Icon name="chart" size={14} /> 일일 브리핑
        </button>
        <button
          className={`inline-flex items-center gap-1 py-2 px-6 border rounded-lg text-[15px] cursor-pointer transition-all duration-200 md:flex-1 md:text-center md:justify-center ${activeTab === 'personal' ? 'bg-[#3B82F6] text-white border-[#3B82F6]' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50 hover:text-gray-900'}`}
          onClick={() => setActiveTab('personal')}
        >
          <Icon name="star" size={14} /> 내 관심 브리핑
        </button>
      </div>

      {/* 테마별 이슈 탭 */}
      {activeTab === 'themes' && (
        <div className="flex flex-col gap-6">
          <div className="flex justify-between items-center pb-4 border-b border-gray-200 md:flex-col md:items-start md:gap-2">
            <p className="text-[15px] text-gray-500 m-0">
              AI가 오늘의 핫 이슈를 테마별로 분석했습니다
            </p>
            <button
              className="inline-flex items-center gap-1 py-2 px-4 bg-[#3B82F6] text-white border-none rounded-lg text-xs cursor-pointer transition-all duration-200 hover:enabled:bg-[#2563EB] disabled:opacity-60 disabled:cursor-not-allowed md:w-full md:justify-center"
              onClick={() => fetchThemeBriefing(true)}
              disabled={themeLoading}
            >
              {themeLoading ? '분석 중...' : <><Icon name="refresh" size={14} /> 새로고침</>}
            </button>
          </div>

          {themeLoading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4 text-gray-500">
              <div className="w-10 h-10 border-[3px] border-gray-200 border-t-blue-500 rounded-full animate-spin"></div>
              <p>AI가 뉴스를 분석 중입니다...</p>
            </div>
          ) : themeBriefing?.themes && themeBriefing.themes.length > 0 ? (
            <>
              <div className="flex flex-col gap-4">
                {themeBriefing.themes.map((theme, idx) => (
                  <div key={idx} className="bg-white border border-gray-200 rounded-2xl p-6 transition-all duration-200 hover:border-[#3B82F6] hover:shadow-[0_4px_12px_rgba(59,130,246,0.1)] md:p-4">
                    <div className="flex items-center gap-2 mb-4 md:flex-wrap">
                      <span className="flex items-center justify-center w-7 h-7 bg-gradient-to-br from-[#3B82F6] to-[#8B5CF6] text-white rounded-full text-xs font-bold">#{idx + 1}</span>
                      <span className="text-lg font-bold text-gray-900">{theme.keyword}</span>
                      <span className="text-xs text-gray-400 ml-auto bg-gray-50 py-1 px-2.5 rounded-xl md:ml-0 md:mt-1 md:w-full md:text-center">{theme.articleCount}건</span>
                    </div>
                    <p className="text-[15px] text-gray-900 leading-[1.7] m-0 mb-4 p-4 bg-gradient-to-br from-[#F0F9FF] to-[#F0FDF4] rounded-lg border-l-[3px] border-[#3B82F6]">{theme.issueSummary}</p>
                    <div className="flex flex-col gap-2">
                      {theme.relatedArticles.map((article, aIdx) => (
                        <a
                          key={aIdx}
                          href={article.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-start gap-2 py-2.5 px-3 bg-gray-50 rounded-lg no-underline transition-all duration-200 hover:bg-[#EFF6FF]"
                        >
                          <span className={`flex-shrink-0 text-[10px] font-medium py-0.5 px-1.5 rounded bg-gray-100 text-gray-500
                            ${article.category === 'entertainment' ? 'bg-pink-100 text-pink-700' : ''}
                            ${article.category === 'sports' ? 'bg-blue-100 text-blue-700' : ''}
                            ${article.category === 'headlines' ? 'bg-red-100 text-red-700' : ''}
                            ${article.category === 'business' ? 'bg-indigo-100 text-indigo-700' : ''}
                            ${article.category === 'technology' ? 'bg-cyan-100 text-cyan-700' : ''}
                            ${article.category === 'economy' || article.category === 'stock' ? 'bg-emerald-100 text-emerald-700' : ''}
                            ${article.category === 'ai' ? 'bg-violet-100 text-violet-700' : ''}
                            ${article.category === 'world' ? 'bg-emerald-50 text-emerald-800' : ''}
                            ${article.category === 'childcare' ? 'bg-amber-100 text-amber-800' : ''}
                            ${article.category === 'education' ? 'bg-purple-100 text-purple-700' : ''}
                            ${article.category === 'healthcare' ? 'bg-pink-100 text-pink-600' : ''}
                          `}>{article.categoryLabel}</span>
                          <span className="text-xs text-gray-900 leading-[1.4] overflow-hidden text-ellipsis line-clamp-2">{article.title}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg text-xs text-gray-500 md:flex-col md:gap-1 md:text-center">
                <span>총 {themeBriefing.totalArticles}개 기사에서 {themeBriefing.totalThemes}개 테마 분석</span>
                {themeBriefing.generatedAt && (
                  <span className="text-gray-400">
                    {formatTime(themeBriefing.generatedAt)} 기준
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="fire" size={48} className="text-[#94a3b8] mb-4" />
              <h3 className="text-lg text-gray-900 m-0 mb-2">분석할 뉴스가 없습니다</h3>
              <p className="text-[15px] text-gray-500 m-0">최근 24시간 내 수집된 뉴스가 부족합니다.</p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'daily' && briefing && (
        <div className="flex flex-col gap-8">
          {/* 핵심 요약 */}
          <section className="bg-white border border-gray-200 rounded-2xl p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0 mb-4">오늘의 핵심 요약</h2>
            {briefing.summary && briefing.summary.length > 0 ? (
              <ul className="list-none p-0 m-0 flex flex-col gap-2">
                {briefing.summary.map((point, idx) => (
                  <li key={idx} className="flex items-start gap-4 p-4 bg-gray-50 rounded-lg">
                    <span className="flex items-center justify-center w-6 h-6 bg-[#3B82F6] text-white rounded-full text-xs font-semibold flex-shrink-0">{idx + 1}</span>
                    <span className="text-[15px] text-gray-900 leading-relaxed">{point}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-gray-400 text-[15px] text-center p-6">오늘 수집된 뉴스가 없습니다.</p>
            )}
          </section>

          {/* 핫 키워드 */}
          <section className="bg-white border border-gray-200 rounded-2xl p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0 mb-4"><Icon name="fire" size={18} /> 핫 키워드</h2>
            {briefing.hotKeywords && briefing.hotKeywords.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {briefing.hotKeywords.map((kw, idx) => (
                  <span
                    key={idx}
                    className={`inline-flex items-center gap-1 py-1 px-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 transition-all duration-200 hover:bg-[#3B82F6] hover:text-white hover:border-[#3B82F6] cursor-pointer
                      ${kw.count >= 5 ? 'text-base font-semibold' : kw.count >= 4 ? 'text-[15px] font-medium' : kw.count >= 3 ? 'text-sm font-medium' : kw.count >= 2 ? 'text-[13px]' : 'text-xs'}
                    `}
                  >
                    #{kw.word}
                    <span className="bg-black/10 py-0.5 px-1.5 rounded-[10px] text-[11px]">{kw.count}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-[15px] text-center p-6">키워드가 없습니다.</p>
            )}
          </section>

          {/* 섹터별 동향 */}
          <section className="bg-white border border-gray-200 rounded-2xl p-6">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0 mb-4"><Icon name="trendingUp" size={18} /> 섹터별 동향</h2>
            {briefing.sectorTrend && Object.keys(briefing.sectorTrend).length > 0 ? (
              <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-2 md:grid-cols-2">
                {Object.entries(briefing.sectorTrend).map(([sector, data]) => (
                  <div key={sector} className={`flex flex-col items-center gap-1 p-4 rounded-lg text-center border-l-[3px]
                    ${data.status === 'positive' ? 'border-l-emerald-500 bg-emerald-500/5' : data.status === 'negative' ? 'border-l-red-500 bg-red-500/5' : 'border-l-gray-400 bg-gray-50'}
                  `}>
                    <span className="text-[15px] font-medium text-gray-900">{sector}</span>
                    <span className="text-xs text-gray-500">{data.label}</span>
                    <span className="text-xs text-gray-400">{data.count}건</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-gray-400 text-[15px] text-center p-6">섹터 데이터가 없습니다.</p>
            )}
          </section>

          {/* 카테고리별 기사 수 */}
          {briefing.categoryBreakdown && briefing.categoryBreakdown.length > 0 && (
            <section className="bg-white border border-gray-200 rounded-2xl p-6">
              <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 m-0 mb-4"><Icon name="folderOpen" size={18} /> 카테고리별 현황</h2>
              <div className="flex flex-col gap-2">
                {briefing.categoryBreakdown.map((cat, idx) => {
                  const maxCount = Math.max(...briefing.categoryBreakdown.map(c => c.count));
                  const width = (cat.count / maxCount) * 100;
                  return (
                    <div key={idx} className="grid grid-cols-[100px_1fr_40px] items-center gap-4 md:grid-cols-[80px_1fr_30px] md:gap-2">
                      <span className="text-xs text-gray-500 text-right">{cat.label}</span>
                      <div className="h-5 bg-gray-50 rounded overflow-hidden">
                        <div className="h-full bg-gradient-to-r from-[#3B82F6] to-[#60A5FA] rounded transition-all duration-500" style={{ width: `${width}%` }}></div>
                      </div>
                      <span className="text-xs text-gray-900 font-medium">{cat.count}</span>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {/* 통계 */}
          <div className="flex justify-center gap-6 p-4 bg-gray-50 rounded-lg">
            <span className="flex items-center gap-1 text-[15px] text-gray-500">
              <Icon name="news" size={16} className="inline-flex text-gray-500" />
              총 {briefing.totalArticles}개 기사 분석
            </span>
          </div>
        </div>
      )}

      {activeTab === 'personal' && (
        <div className="flex flex-col gap-6">
          {personalBriefing?.results && personalBriefing.results.length > 0 ? (
            personalBriefing.results.map((result, idx) => (
              <section key={idx} className="bg-white border border-gray-200 rounded-2xl p-6">
                <h3 className="flex items-center gap-2 flex-wrap text-base font-semibold text-gray-900 m-0 mb-2">
                  <span className="inline-block py-1 px-2 bg-blue-500 text-white rounded-lg text-[15px]">"{result.keyword}"</span>
                  <span className="text-xs text-gray-400 font-normal">{result.count}개 기사</span>
                </h3>
                <ul className="list-none p-0 m-0 flex flex-col gap-1">
                  {result.articles.map((article, aIdx) => (
                    <li key={aIdx} className="flex justify-between items-center gap-4 p-2 bg-gray-50 rounded md:flex-col md:items-start md:gap-1">
                      <a href={article.link} target="_blank" rel="noopener noreferrer" className="text-gray-900 no-underline text-[15px] flex-1 overflow-hidden text-ellipsis whitespace-nowrap hover:text-blue-500 md:whitespace-normal">
                        {article.title}
                      </a>
                      <span className="text-xs text-gray-400 flex-shrink-0">{article.source}</span>
                    </li>
                  ))}
                </ul>
              </section>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Icon name="star" size={48} className="text-[#94a3b8] mb-4" />
              <h3 className="text-lg text-gray-900 m-0 mb-2">관심 키워드를 등록해주세요</h3>
              <p className="text-[15px] text-gray-500 m-0 leading-relaxed">뉴스 페이지에서 관심 키워드를 등록하면<br/>맞춤 브리핑을 받아볼 수 있습니다.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NewsBriefing;
